import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { DetailsModal, FilterModal, INITIAL_FILTER, filterCount, periodTitle } from "../../widgets/queue"

/* Shared */
import { duration, number, queueIcon, sourceOf, statusOf } from "../../widgets/queue/shared"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Constants */
import { env } from "../../constants"

/* Moment */
import moment from "moment"
import "moment/locale/ru"

/* REST API */
import { list } from "../../api/Queue"

moment.locale("ru")


/* Page Queues — the tickets of the whole platform. A queue lives one day, so the page opens
   on today and everything else is chosen in the window of the filters */
class Queues extends Component {

    constructor() {
        super()

        this.state = {

            data: [],
            status: "loading",
            count: 0,
            page: 1,
            statistics: null,
            search: "",
            filter: { ...INITIAL_FILTER }
        }

        /* The card of the ticket and the filters are opened by the reference */
        this._details = createRef()
        this._filter = createRef()
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load()
    }

    /* Load action */
    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, filter } = this.state

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        /* Set to REST API */
        list({
            page,
            search,
            organizationID: filter.organizationID,
            status: filter.status,
            source: filter.source,
            startDate: moment(filter.startDate).format("YYYY-MM-DD"),
            endDate: moment(filter.endDate).format("YYYY-MM-DD")
        }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, count: response.data.count, statistics: response.data.statistics })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, statistics: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, statistics: null })
        })
    }

    /* On Submit */
    search = event => {
        event.preventDefault()
        this.load(1)
    }

    /* Search Action */
    searchText = text => {
        if (text === "") {
            this.setState({ search: text }, () => this.load(1))
        }
        else {
            this.setState({ search: text })
        }
    }

    /* Open the card of the ticket */
    open = item => {
        this._details.current.open(item)
    }

    /* Draw one ticket of the table */
    _item = (item, index) => {

        const image = item.organizationImage ? `${env.mediapoint}organizations/${item.organizationImage}` : null
        const state = statusOf(item.status)

        /* How long the client waited for his call — a ticket which is still waiting counts
           the minutes from the moment it was taken */
        const waited = item.calledAt
            ? moment(item.calledAt).diff(moment(item.createdAt), "minutes")
            : (item.status === "WAITING" ? moment().diff(moment(item.createdAt), "minutes") : null)

        return (
            <div onClick={() => this.open(item)} className="queue-item" key={`${index}`}>

                <div className="queue-item-cell client">

                    <Avatar className="queue-item-avatar" src={image} alt={item.organization}>
                        {queueIcon()}
                    </Avatar>

                    <div className="queue-item-title">
                        <div className="queue-item-name">
                            {utils.check(item.clientName, "Без имени")}
                            <div className="queue-item-badge">№{utils.check(item.ticketNumber, item.id)}</div>
                        </div>
                        <div className="queue-item-text">{utils.check(item.phone, "—")}</div>
                    </div>

                </div>

                <div className="queue-item-cell organization">
                    <div className="queue-item-value">{utils.check(item.organization, "Организация удалена")}</div>
                    <div className="queue-item-text">{item.member ? item.member : "Мастер не назначен"}</div>
                </div>

                <div className="queue-item-cell time">
                    <div className="queue-item-value">{moment(item.createdAt).format("HH:mm")}</div>
                    <div className="queue-item-text">{moment(item.createdAt).format("DD.MM.YYYY")}</div>
                </div>

                <div className="queue-item-cell service">
                    <div className="queue-item-value">{utils.check(item.service, "Услуга не выбрана")}</div>
                    <div className="queue-item-text">{sourceOf(item.source)}</div>
                </div>

                <div className="queue-item-cell waiting">
                    {waited !== null
                        ? <>
                            <div className="queue-item-value">{duration(waited)}</div>
                            <div className="queue-item-text">{item.calledAt ? "ждал вызова" : "уже ждёт"}</div>
                        </>
                        : <div className="queue-item-text">—</div>
                    }
                </div>

                <div className="queue-item-cell state">
                    <div className={`queue-item-state ${state.accent}`}>
                        <i />
                        {state.text}
                    </div>
                </div>

                <div className="queue-item-cell arrow">
                    <Icon name="right" size={15} width={2} />
                </div>

            </div>
        )
    }

    /* The numbers of the chosen period — one slim line over the table */
    _summary = () => {

        /* Fields */
        const { statistics, filter } = this.state

        /* Check Data */
        if (!statistics) {
            return null
        }

        return (
            <div className="queue-summary">

                <div className="queue-summary-period">
                    <span>Период</span>
                    {periodTitle(filter)}
                </div>

                <div className="queue-summary-values">
                    <div className="queue-summary-value">Всего <b>{number(statistics.total)}</b></div>
                    <div className="queue-summary-value">В очереди <b className="orange">{number(statistics.waiting) + number(statistics.called) + number(statistics.serving)}</b></div>
                    <div className="queue-summary-value">Обслужены <b className="green">{number(statistics.done)}</b></div>
                    <div className="queue-summary-value">Ушли <b className="red">{number(statistics.missed) + number(statistics.cancelled)}</b></div>
                    <div className="queue-summary-value">Среднее ожидание <b className="blue">{duration(statistics.waitMinutes)}</b></div>
                </div>

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, page } = this.state

        /* Layout */
        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {status === "ok" && this._summary()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.setState({ search: "", filter: { ...INITIAL_FILTER } }, () => this.load(1, true))} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._summary()}

                <div className="queue-list">

                    <div className="queue-item head">
                        <div className="queue-item-cell client">Клиент</div>
                        <div className="queue-item-cell organization">Организация</div>
                        <div className="queue-item-cell time">Встал</div>
                        <div className="queue-item-cell service">Услуга</div>
                        <div className="queue-item-cell waiting">Ожидание</div>
                        <div className="queue-item-cell state">Статус</div>
                        <div className="queue-item-cell arrow" />
                    </div>

                    {data.map((item, index) => this._item(item, index))}

                </div>

                <Pagination size={20} page={page} total={count} onPageChange={page => this.load(page, true)} />

            </div>
        )
    }

    /* Draw Header */
    _header = () => {

        /* Fields */
        const { search, filter } = this.state

        const active = filterCount(filter)

        return (
            <div className="queue-header">

                <div className="page-header-text">Очередь</div>

                <div className="queue-header-tools">

                    <form onSubmit={this.search} className="queue-search">

                        <div className="queue-search-icon">
                            <Icon name="search" size={17} />
                        </div>

                        <input placeholder="Талон, имя, телефон, организация" value={search} onChange={event => this.searchText(event.target.value)} />

                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="queue-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }

                    </form>

                    <div onClick={() => this._filter.current.open(filter)} title="Фильтр" className={`queue-filter-button ${active > 0 ? "active" : ""}`}>
                        <Icon name="filter" size={19} />
                        {active > 0 && <div className="queue-filter-count">{active}</div>}
                    </div>

                </div>

            </div>
        )
    }

    render = () => {

        return (
            <div className="page">

                {/* Draw Header */}
                {this._header()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Details */}
                <DetailsModal ref={this._details} onChanged={() => this.load(this.state.page)} />

                {/* Draw Filters */}
                <FilterModal ref={this._filter} onApply={filter => this.setState({ filter }, () => this.load(1, true))} />

            </div>
        )
    }
}

export default withRouter(Queues)
